import {
  BaseQueryFn,
  FetchArgs,
  createApi,
  fetchBaseQuery
} from '@reduxjs/toolkit/query/react'
import { AppState } from './redux'
import { createLogger } from '@/common/logger'
import { EstimateTaxPayload, PostTransactionPayload, PostTransactionResponse, TaxEstimate } from '@/modules/checkout/types/checkout'
import { Entry } from "@/modules/entry/types/entry";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { cacheToken } from "@/modules/session/sessionSlice";

const logger = createLogger({ prefix: 'ctliveApi' })

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.CTLIVE_API_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as AppState).session.token;
    logger.warn('token')
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    } else {
      logger.warn('No token available for API call')
    }
    if (typeof window === 'undefined') {
      headers.set('Referer', process.env.STOREFRONT_URL)
    }
    return headers
  }
})

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  let result = await baseQuery(args, api, extraOptions)

  if (result.error && result.error.status === 401) {
    if ((args as FetchArgs)?.url === 'registration/createAnonymousSession') {
      return result
    }

    try {
      logger.info('Token expired, attempting refresh...')
      const refreshResult = await baseQuery(
        {
          url: 'registration/createAnonymousSession',
          method: 'POST',
          body: { clientId: 'storefront' },
        },
        api,
        extraOptions
      )

      if (refreshResult.data) {
        api.dispatch(
          cacheToken((refreshResult.data as { token: string }).token)
        )
        result = await baseQuery(args, api, extraOptions)
      } else {
        logger.error('Token refresh failed', refreshResult.error)
      }
    } catch (error) {
      logger.error('Token refresh failed', error)
    }
  }
  return result
}

export const ctliveApi = createApi({
  reducerPath: 'ctliveApi',
  baseQuery: baseQueryWithReauth,
  endpoints: build => ({
    createAnonymousSession: build.mutation<{ token: string }, void>({
      query: () => ({
        url: 'registration/createAnonymousSession',
        method: 'POST',
        body: {
          clientId: 'storefront'
        }
      })
    }),
    estimateTax: build.mutation<TaxEstimate, EstimateTaxPayload>({
      query: (body) => ({
        url: 'registration/estimateTax',
        method: 'POST',
        body
      })
    }),
    postTransaction: build.mutation<PostTransactionResponse, PostTransactionPayload>({
      query: (body) => ({
        url: 'storefront/transaction',
        method: 'POST',
        body
      })
    }),
    postTransactionConfirm: build.mutation<PostTransactionResponse, {
      regTransactionId: number,
      paymentIntentId: string,
    }>({
      query: (body) => ({
        url: `storefront/transaction/confirm`,
        method: 'POST',
        body
      })
    }),
    resendEmail: build.mutation<{ emailId: string }, any>({
      query: (body) => ({
        url: 'email/resend',
        method: 'POST',
        body
      })
    }),
    getEntry: build.query<Entry, number>({
      query: (entryId) => ({
        url: `registration/entry/${entryId}`,
        method: 'GET',
      }),
    }),
  })
})

export const {
  useCreateAnonymousSessionMutation,
  useEstimateTaxMutation,
  usePostTransactionMutation,
  useResendEmailMutation,
  useGetEntryQuery,
  usePostTransactionConfirmMutation,
} = ctliveApi
